import type { ReactNode } from 'react';
import { createRoute, redirect, useNavigate, type AnyRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { links, listSearchSchema } from '@eis/core/reporting';
import { reportingPermissions as p } from '../permissions';
import { ReportsListPage } from '../features/reports-list/ui/ReportsListPage';
import { ReportViewerPage } from '../features/report-viewer/ui/ReportViewerPage';
import { ReportEditorPage } from '../features/report-editor/ui/ReportEditorPage';
import { DatasetsPage, type DatasetsSearch } from '../features/datasets/ui/DatasetsPage';
import { DatasetBuilderPage } from '../features/dataset-builder/ui/DatasetBuilderPage';
import { SettingsPage } from '../features/settings/ui/SettingsPage';

/**
 * The reporting route tree (SDD-024 A-5; modules/reporting/PERMISSIONS.md "Route groups"):
 * `/reporting/reports`, `/reporting/reports/$code`, `/reporting/reports/$code/edit`,
 * `/reporting/datasets`, `/reporting/datasets/$code/edit`, `/reporting/settings`.
 *
 * Permissions are declared ONCE here in `staticData`; `beforeLoad` enforces them and the manifest's
 * nav is filtered from the same declarations (Addendum A.2). The viewer's search carries the
 * editable filter values as `f.<index>` (D-2), any number of them, validated as strings; the
 * datasets page keeps its selection and search in the URL.
 */
interface GuardContext {
  readonly permissions: { canGroups: (groups: readonly (readonly string[])[]) => boolean };
}

const guard =
  (groups: readonly (readonly string[])[]) =>
  ({ context }: { context: GuardContext }): void => {
    if (!context.permissions.canGroups(groups)) {
      // TanStack signals navigation by throwing this sentinel — control flow, not an error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/403' });
    }
  };

/**
 * `f.<index>` → value: the viewer's editable filter values (D-2); `sort` (a table header) and `tz`
 * (`utc` | `local`, how date-times read); everything else is dropped.
 */
const viewerSearchSchema = z
  .record(z.string(), z.unknown())
  .transform(
    (raw) =>
      Object.fromEntries(
        Object.entries(raw).flatMap(([key, value]) =>
          (/^f\.\d+$/.test(key) || key === 'sort' || key === 'tz') &&
          (typeof value === 'string' || typeof value === 'number')
            ? [[key, String(value)]]
            : [],
        ),
      ) as Record<string, string>,
  );

const datasetsSearchSchema = z.object({
  dataset: z.string().optional(),
  custom: z.string().optional(),
  q: z.string().optional(),
});

interface CodeParams {
  readonly code: string;
}

export function createRoutes(parentRoute: AnyRoute): AnyRoute[] {
  const reportsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/reports',
    validateSearch: listSearchSchema,
    staticData: { permissions: [[p.reports.view]] },
    beforeLoad: guard([[p.reports.view]]),
    component: ReportsListPage,
  });

  const reportViewer = createRoute({
    getParentRoute: () => parentRoute,
    path: '/reports/$code',
    validateSearch: viewerSearchSchema,
    staticData: { permissions: [[p.reports.view]] },
    beforeLoad: guard([[p.reports.view]]),
    component: function ReportViewerRoute(): ReactNode {
      const navigate = useNavigate();
      const { code } = reportViewer.useParams() as unknown as CodeParams;
      const search = reportViewer.useSearch() as unknown as Record<string, string>;
      return (
        <ReportViewerPage
          key={code}
          code={code}
          search={search}
          onSearchChange={(next) => {
            void navigate({ href: links.reporting.report(code, next), replace: true });
          }}
        />
      );
    },
  });

  const reportEditor = createRoute({
    getParentRoute: () => parentRoute,
    path: '/reports/$code/edit',
    staticData: { permissions: [[p.reports.update]] },
    beforeLoad: guard([[p.reports.update]]),
    component: function ReportEditorRoute(): ReactNode {
      const { code } = reportEditor.useParams() as unknown as CodeParams;
      return <ReportEditorPage key={code} code={code} />;
    },
  });

  const datasetsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/datasets',
    validateSearch: datasetsSearchSchema,
    staticData: { permissions: [[p.datasets.view]] },
    beforeLoad: guard([[p.datasets.view]]),
    component: function DatasetsRoute(): ReactNode {
      const navigate = useNavigate();
      const search = datasetsIndex.useSearch() as unknown as DatasetsSearch;
      return (
        <DatasetsPage
          search={search}
          onSearchChange={(next) => {
            const query = new URLSearchParams(
              Object.entries(next).filter(
                (entry): entry is [string, string] =>
                  typeof entry[1] === 'string' && entry[1] !== '',
              ),
            ).toString();
            void navigate({
              href:
                query === ''
                  ? links.reporting.datasets()
                  : `${links.reporting.datasets()}?${query}`,
              replace: true,
            });
          }}
        />
      );
    },
  });

  const datasetBuilder = createRoute({
    getParentRoute: () => parentRoute,
    path: '/datasets/$code/edit',
    staticData: { permissions: [[p.datasets.manage]] },
    beforeLoad: guard([[p.datasets.manage]]),
    component: function DatasetBuilderRoute(): ReactNode {
      const { code } = datasetBuilder.useParams() as unknown as CodeParams;
      return <DatasetBuilderPage key={code} code={code} />;
    },
  });

  const settings = createRoute({
    getParentRoute: () => parentRoute,
    path: '/settings',
    staticData: { permissions: [[p.settings.manage]] },
    beforeLoad: guard([[p.settings.manage]]),
    component: SettingsPage,
  });

  return [reportsIndex, reportViewer, reportEditor, datasetsIndex, datasetBuilder, settings];
}
